import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Copy, Download, Printer, RotateCcw, Info, Zap } from "lucide-react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import {
  CONFIG,
  STEPS,
  PRESETS,
  initialAnswers,
  buildMusterabsatz,
  buildFragenAnBerater,
  buildProfil,
} from "@/config/nachhaltigkeitAssistent";

// -------- Kleine Bausteine ----------
function ProgressBar({ step, total }) {
  const pct = Math.round((step / (total - 1)) * 100);
  return (
    <div aria-live="polite" className="mb-8" data-testid="wizard-progress">
      <div className="flex justify-between text-xs text-neutral-600 mb-2">
        <span>Schritt {step + 1} von {total}</span>
        <span>{pct} %</span>
      </div>
      <div className="h-1 bg-neutral-200">
        <div className="h-1 bg-brand-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ExplainerDetails({ title, children }) {
  return (
    <details className="mt-4 border border-neutral-200 bg-white group open:border-brand-primary">
      <summary className="cursor-pointer px-5 py-3 text-sm font-medium text-brand-dark list-none flex items-center gap-2 justify-between">
        <span className="inline-flex items-center gap-2"><Info size={16} strokeWidth={1.75} className="text-brand-primary" /> {title}</span>
        <span className="text-brand-primary group-open:rotate-45 transition-transform">+</span>
      </summary>
      <div className="px-5 pb-4 text-sm text-neutral-700 leading-relaxed">{children}</div>
    </details>
  );
}

function OptionButton({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={selected}
      className={`w-full text-left border p-4 transition-colors ${
        selected ? "border-brand-primary bg-brand-paper" : "border-neutral-200 bg-white hover:border-brand-primary"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={`w-4 h-4 rounded-full border ${selected ? "bg-brand-primary border-brand-primary" : "border-neutral-300"}`} />
        <span className="text-neutral-800">{children}</span>
      </div>
    </button>
  );
}

function CheckboxItem({ checked, onChange, children, testId }) {
  return (
    <label className="flex items-start gap-3 p-3 border border-neutral-200 bg-white cursor-pointer hover:border-brand-primary">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-testid={testId}
        className="mt-1 h-4 w-4 accent-brand-primary"
      />
      <span className="text-sm text-neutral-800">{children}</span>
    </label>
  );
}

function StepNav({ onBack, onNext, onSkip, nextDisabled, backDisabled, nextLabel = "Weiter" }) {
  return (
    <div className="mt-8 flex flex-wrap items-center gap-3 justify-between">
      <button
        type="button"
        onClick={onBack}
        disabled={backDisabled}
        className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-brand-primary disabled:opacity-40"
        data-testid="wizard-back"
      >
        <ArrowLeft size={16} strokeWidth={1.75} /> Zurück
      </button>
      <div className="flex gap-3">
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="px-4 py-2 text-sm text-neutral-600 hover:text-brand-primary"
            data-testid="wizard-skip"
          >
            Überspringen / Weiß ich nicht
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          className="inline-flex items-center gap-2 bg-brand-primary text-white px-5 py-2.5 text-sm font-medium hover:bg-brand-hover disabled:opacity-40"
          data-testid="wizard-next"
        >
          {nextLabel} <ArrowRight size={16} strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}

function Slider({ value, onChange, min = 5, max = 100, step = 5 }) {
  return (
    <div className="mt-4">
      <div className="flex items-center gap-4">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          className="flex-1 accent-brand-primary"
          data-testid="wizard-slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
        />
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
          }}
          className="w-20 border border-neutral-300 px-2 py-1 text-right font-mono"
          data-testid="wizard-slider-input"
          aria-label="Mindestanteil in Prozent (Zahleneingabe)"
        />
        <span className="text-neutral-700">%</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {[5, 10, 25, 50].map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => onChange(preset)}
            className={`px-3 py-1 text-xs border ${value === preset ? "border-brand-primary bg-brand-primary text-white" : "border-neutral-300 hover:border-brand-primary"}`}
            data-testid={`wizard-preset-${preset}`}
          >
            {preset} %
          </button>
        ))}
      </div>
      {value >= 25 && (
        <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">
          Hinweis: Hohe Mindestanteile können die Auswahl passender Produkte stark einschränken.
        </p>
      )}
    </div>
  );
}

// -------- PDF-Erzeugung ----------
function generatePdf(answers) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentW = pageW - margin * 2;
  const green = [62, 87, 67];
  const neutralDark = [45, 45, 45];
  const neutralLight = [110, 110, 110];

  const drawHeader = (pageNo) => {
    doc.setFontSize(9);
    doc.setTextColor(...neutralLight);
    doc.text("Grüne Geldanlage · Nachhaltigkeitspräferenz-Assistent", margin, 30);
    doc.text(`Seite ${pageNo} von 2`, pageW - margin, 30, { align: "right" });
    doc.setDrawColor(...green);
    doc.line(margin, 36, pageW - margin, 36);
  };

  const drawFooter = () => {
    doc.setFontSize(8);
    doc.setTextColor(...neutralLight);
    const y = pageH - 30;
    doc.setDrawColor(...green);
    doc.line(margin, y - 10, pageW - margin, y - 10);
    doc.text("gruene-geldanlage.com", margin, y);
    doc.text(CONFIG.meta.stand ? `Stand: ${CONFIG.meta.stand}` : "", pageW / 2, y, { align: "center" });
    doc.text("Vorbereitung – keine Anlageberatung", pageW - margin, y, { align: "right" });
  };

  const h1 = (text, y) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...green);
    doc.text(text, margin, y);
    doc.setFont("helvetica", "normal");
    return y + 22;
  };
  const h2 = (text, y) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...green);
    doc.text(text, margin, y);
    doc.setFont("helvetica", "normal");
    return y + 16;
  };
  const p = (text, y, size = 10, color = neutralDark, maxW = contentW) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxW);
    doc.text(lines, margin, y);
    return y + lines.length * (size + 2);
  };

  // Seite 1
  drawHeader(1);
  let y = 60;
  y = h1("Meine Nachhaltigkeitspräferenz", y);
  y = p("Vorbereitung auf das Beratungsgespräch", y, 11, green) + 6;

  const name = answers.name?.trim();
  const dateFmt = (() => {
    const [yy, mm, dd] = (answers.datum || "").split("-");
    return dd && mm && yy ? `${dd}.${mm}.${yy}` : "";
  })();
  y = p(`${name ? `Name: ${name}` : ""}${name && dateFmt ? "   ·   " : ""}${dateFmt ? `Datum: ${dateFmt}` : ""}`, y, 10, neutralDark);
  y += 12;

  y = h2("Meine Vorgaben", y);
  const rows = buildProfil(answers);
  // Simple table
  const colW1 = 200;
  const rowH = 22;
  rows.forEach((r) => {
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageW - margin, y);
    doc.setFontSize(10);
    doc.setTextColor(...neutralDark);
    doc.setFont("helvetica", "bold");
    doc.text(r.k, margin + 4, y + 14);
    doc.setFont("helvetica", "normal");
    const vLines = doc.splitTextToSize(r.v || "—", contentW - colW1 - 8);
    doc.text(vLines, margin + colW1 + 4, y + 14);
    const rowActualH = Math.max(rowH, vLines.length * 12 + 8);
    y += rowActualH;
  });
  doc.line(margin, y, pageW - margin, y);
  y += 18;

  y = h2("Flexibilität und Hinweise", y);
  const flex =
    answers.flexibilitaet === "anpassen"
      ? "Ich bin bereit, Vorgaben im Gespräch anzupassen, falls kein Produkt alle Kriterien erfüllt."
      : answers.flexibilitaet === "verzichten"
        ? "Ich verzichte lieber auf ein Investment, als Vorgaben zu lockern."
        : answers.flexibilitaet === "unsicher"
          ? "Wie ich mit Konflikten umgehe, möchte ich im Gespräch klären."
          : "—";
  y = p(flex, y, 10);
  y += 10;
  y = p(CONFIG.disclaimer, y, 8, neutralLight);
  drawFooter();

  // Seite 2
  doc.addPage();
  drawHeader(2);
  y = 60;
  y = h1("Musterabsatz für das Gespräch", y);
  y = p(buildMusterabsatz(answers), y, 10, neutralDark) + 12;

  y = h2("Fragen an meinen Berater", y);
  const fragen = buildFragenAnBerater(answers);
  fragen.forEach((f, i) => {
    const boxSize = 8;
    doc.setDrawColor(...green);
    doc.rect(margin, y - 8, boxSize, boxSize);
    const lines = doc.splitTextToSize(`${i + 1}. ${f}`, contentW - boxSize - 8);
    doc.setFontSize(10);
    doc.setTextColor(...neutralDark);
    doc.text(lines, margin + boxSize + 6, y);
    y += lines.length * 12 + 4;
    if (y > pageH - 180) return; // Notizen-Feld muss reinpassen
  });

  y += 8;
  y = h2("Notizen aus dem Gespräch", y);
  // 6 Linien
  for (let i = 0; i < 6; i++) {
    y += 14;
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageW - margin, y);
  }
  drawFooter();

  const dateStr = (answers.datum || new Date().toISOString().slice(0, 10)).replace(/-/g, "-");
  doc.save(`Nachhaltigkeitspraeferenz-Vorbereitung-${dateStr}.pdf`);
}

// -------- Haupt-Komponente ----------
export default function NachhaltigkeitAssistent() {
  const [answers, setAnswers] = useState(initialAnswers);
  const [stepIdx, setStepIdx] = useState(0); // 0 = Intro, ..., 9 = Ergebnis
  const [presetKey, setPresetKey] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [stepIdx]);

  const go = (n) => setStepIdx((s) => Math.max(0, Math.min(STEPS.length - 1, s + n)));
  const goTo = (i) => setStepIdx(Math.max(0, Math.min(STEPS.length - 1, i)));

  const reset = () => {
    setAnswers(initialAnswers());
    setPresetKey(null);
    setStepIdx(0);
  };

  const setA = (patch) => setAnswers((prev) => ({ ...prev, ...patch, }));

  // Preset laden → prefill + direkt zur Ergebnisseite
  const applyPreset = (p) => {
    setAnswers(p.build());
    setPresetKey(p.key);
    setStepIdx(STEPS.length - 1);
  };

  // ---------- Render pro Schritt ----------
  const step = STEPS[stepIdx];

  return (
    <div ref={containerRef} className="max-w-3xl mx-auto" data-testid="assistent-container">
      {stepIdx > 0 && stepIdx < STEPS.length - 1 && (
        <ProgressBar step={stepIdx} total={STEPS.length - 1} />
      )}

      {/* Schritt 0 – Intro */}
      {step.key === "intro" && (
        <section className="bg-white border border-neutral-200 p-8 md:p-10" data-testid="wizard-intro">
          <div className="overline mb-3">Werkzeug · kostenfrei</div>
          <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-4">{CONFIG.intro.title}</h2>
          <p className="text-lg text-neutral-700 leading-relaxed mb-6">{CONFIG.intro.lead}</p>
          <ul className="space-y-2 text-sm text-neutral-700 mb-8">
            {CONFIG.intro.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2"><Check size={16} strokeWidth={2} className="text-brand-primary mt-0.5 shrink-0" /> {b}</li>
            ))}
          </ul>
          <div className="text-xs bg-brand-paper border border-neutral-200 p-3 mb-8 text-neutral-700">
            <strong>{CONFIG.meta.displayStand}</strong> Details in unserem Artikel{" "}
            <Link to="/artikel/sfdr-2-0-reform" className="text-brand-primary underline">SFDR 2.0</Link>.
          </div>

          <button
            type="button"
            onClick={() => go(1)}
            className="inline-flex items-center gap-2 bg-brand-primary text-white px-6 py-3 text-sm font-medium hover:bg-brand-hover"
            data-testid="wizard-start"
          >
            {CONFIG.intro.ctaLabel} <ArrowRight size={16} strokeWidth={1.75} />
          </button>

          {/* Ein-Klick-Profile */}
          <div className="mt-12 pt-10 border-t border-neutral-200" data-testid="presets-section">
            <div className="flex items-start gap-2 mb-2">
              <Zap size={18} strokeWidth={1.75} className="text-brand-primary mt-0.5" />
              <div>
                <h3 className="font-serif text-xl font-medium text-brand-dark">Oder mit einem Klick starten</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Drei fertige Profile als Vorbelegung. Sie springen direkt zur Ergebnisseite –
                  dort können Sie das PDF herunterladen oder mit „Neu starten" alles verwerfen.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className="text-left border border-neutral-200 bg-white p-5 hover:border-brand-primary hover:shadow-sm transition-all flex flex-col"
                  data-testid={`preset-${p.key}`}
                >
                  <div className="overline mb-2">Profil</div>
                  <div className="font-serif text-lg font-medium text-brand-dark mb-1">{p.title}</div>
                  <div className="text-xs text-brand-primary mb-3">{p.tagline}</div>
                  <p className="text-sm text-neutral-600 leading-relaxed flex-1">{p.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand-primary font-medium">
                    Profil laden <ArrowRight size={14} strokeWidth={1.75} />
                  </span>
                </button>
              ))}
            </div>
            <p className="text-xs text-neutral-500 mt-4">
              Die Profile sind Muster­vorbelegungen – keine Anlage­empfehlung. Passen Sie sie im Gespräch mit Ihrer Bank an.
            </p>
          </div>

          <p className="mt-8 text-xs text-neutral-500">{CONFIG.privacy}</p>
        </section>
      )}

      {/* Schritt 1 – Grundfrage */}
      {step.key === "grundfrage" && (
        <section className="bg-white border border-neutral-200 p-8 md:p-10" data-testid="wizard-grundfrage">
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-6">Sollen bei Ihrer Geldanlage Nachhaltigkeitsaspekte berücksichtigt werden?</h2>
          <div className="space-y-3">
            <OptionButton selected={answers.grundfrage === "ja"} onClick={() => setA({ grundfrage: "ja" })} testId="grundfrage-ja">
              Ja, mir ist Nachhaltigkeit wichtig
            </OptionButton>
            <OptionButton selected={answers.grundfrage === "nein"} onClick={() => setA({ grundfrage: "nein" })} testId="grundfrage-nein">
              Nein, nicht relevant für mich
            </OptionButton>
            <OptionButton selected={answers.grundfrage === "unsicher"} onClick={() => setA({ grundfrage: "unsicher" })} testId="grundfrage-unsicher">
              Ich bin unsicher und möchte es im Gespräch klären
            </OptionButton>
          </div>
          <ExplainerDetails title="Was heißt das?">
            <p>Ihre Bank oder Ihr Berater ist seit dem 2. August 2022 verpflichtet, danach zu fragen. Sie sind aber nicht verpflichtet, Nachhaltigkeit als Kriterium zu wählen. <strong>Beispiel:</strong> Wenn Sie „Nein" wählen, wird das im Beratungsprotokoll dokumentiert – Sie können es später jederzeit ändern.</p>
          </ExplainerDetails>
          <StepNav
            onBack={() => go(-1)}
            onNext={() => {
              if (answers.grundfrage === "nein") goTo(STEPS.length - 1);
              else go(1);
            }}
            nextDisabled={!answers.grundfrage}
          />
        </section>
      )}

      {/* Schritt 2 – Drei Wege */}
      {step.key === "erklaerung" && (
        <section className="bg-white border border-neutral-200 p-8 md:p-10" data-testid="wizard-erklaerung">
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">Drei Wege, Nachhaltigkeit im Beratungsgespräch festzulegen</h2>
          <p className="text-neutral-700 leading-relaxed mb-6">
            Ein Produkt erfüllt Ihre Nachhaltigkeitspräferenz, wenn es mindestens einen der drei Wege abdeckt.
            Sie können einen, mehrere oder alle wählen.
          </p>
          <ol className="space-y-4">
            <li className="border-l-4 border-brand-primary bg-brand-paper p-4">
              <div className="font-medium text-brand-dark">1. EU-Taxonomie</div>
              <div className="text-sm text-neutral-700">Mindestanteil des Geldes in Wirtschafts­tätigkeiten, die die EU als ökologisch nachhaltig einstuft.</div>
            </li>
            <li className="border-l-4 border-brand-primary bg-brand-paper p-4">
              <div className="font-medium text-brand-dark">2. Nachhaltige Investitionen (SFDR)</div>
              <div className="text-sm text-neutral-700">Mindestanteil in „nachhaltige Investitionen" im Sinne der EU-Offenlegungs­verordnung (Art. 2 Nr. 17).</div>
            </li>
            <li className="border-l-4 border-brand-primary bg-brand-paper p-4">
              <div className="font-medium text-brand-dark">3. PAI-Berücksichtigung</div>
              <div className="text-sm text-neutral-700">Berücksichtigung der wichtigsten nachteiligen Auswirkungen (Principal Adverse Impacts) auf Nachhaltigkeitsfaktoren.</div>
            </li>
          </ol>
          <ExplainerDetails title="Was heißt das?">
            <p>Die drei Wege stammen aus der Delegierten Verordnung (EU) 2017/565, geändert durch (EU) 2021/1253. Eine Einstufung als Artikel 8 oder 9 (SFDR) reicht für die Nachhaltigkeitspräferenz allein nicht aus – es braucht konkrete Mindestanteile oder eine PAI-Berücksichtigung. <strong>Beispiel:</strong> Ein Artikel-8-Fonds ohne quantifizierten Mindestanteil erfüllt Ihre Präferenz nicht automatisch.</p>
          </ExplainerDetails>
          <StepNav onBack={() => go(-1)} onNext={() => go(1)} />
        </section>
      )}

      {/* Schritt 3 – EU-Taxonomie */}
      {step.key === "taxonomie" && (
        <section className="bg-white border border-neutral-200 p-8 md:p-10" data-testid="wizard-taxonomie">
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">Soll ein Mindestanteil in EU-taxonomiekonforme Aktivitäten fließen?</h2>
          <p className="text-sm text-neutral-600 mb-6">Weg 1 von 3.</p>
          <div className="space-y-3">
            <OptionButton selected={!answers.taxonomie.active} onClick={() => setA({ taxonomie: { ...answers.taxonomie, active: false } })} testId="tax-nicht">
              Nicht wichtig
            </OptionButton>
            <OptionButton selected={answers.taxonomie.active} onClick={() => setA({ taxonomie: { ...answers.taxonomie, active: true } })} testId="tax-ja">
              Ja, mindestens …
            </OptionButton>
          </div>
          {answers.taxonomie.active && (
            <Slider
              value={answers.taxonomie.min}
              onChange={(v) => setA({ taxonomie: { ...answers.taxonomie, min: v } })}
            />
          )}
          <ExplainerDetails title="Was heißt das?">
            <p>Die EU-Taxonomie ist ein Regelwerk, das eine wirtschaftliche Tätigkeit als „ökologisch nachhaltig" einstuft, wenn sie zu einem der sechs EU-Umweltziele wesentlich beiträgt, keinem erheblich schadet und Mindestschutz-Kriterien erfüllt. <strong>Beispiel:</strong> Ein Windpark-Betreiber kann taxonomiekonform sein; eine Ölfirma in der Regel nicht.</p>
          </ExplainerDetails>
          <StepNav
            onBack={() => go(-1)}
            onNext={() => go(1)}
            onSkip={() => { setA({ taxonomie: { active: false, min: 25 } }); go(1); }}
          />
        </section>
      )}

      {/* Schritt 4 – Nachhaltige Investitionen */}
      {step.key === "nachhaltige" && (
        <section className="bg-white border border-neutral-200 p-8 md:p-10" data-testid="wizard-nachhaltige">
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">Soll ein Mindestanteil in „nachhaltige Investitionen" (SFDR) fließen?</h2>
          <p className="text-sm text-neutral-600 mb-6">Weg 2 von 3.</p>
          <div className="space-y-3">
            <OptionButton selected={!answers.nachhaltige.active} onClick={() => setA({ nachhaltige: { ...answers.nachhaltige, active: false } })} testId="nh-nicht">
              Nicht wichtig
            </OptionButton>
            <OptionButton selected={answers.nachhaltige.active} onClick={() => setA({ nachhaltige: { ...answers.nachhaltige, active: true } })} testId="nh-ja">
              Ja, mindestens …
            </OptionButton>
          </div>
          {answers.nachhaltige.active && (
            <>
              <Slider
                value={answers.nachhaltige.min}
                onChange={(v) => setA({ nachhaltige: { ...answers.nachhaltige, min: v } })}
              />
              <div className="mt-6">
                <div className="text-sm font-medium text-neutral-700 mb-2">Schwerpunkt (optional)</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { k: "egal", l: "egal" },
                    { k: "oeko", l: "eher ökologisch" },
                    { k: "sozial", l: "eher sozial" },
                  ].map(({ k, l }) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setA({ nachhaltige: { ...answers.nachhaltige, schwerpunkt: k } })}
                      className={`px-3 py-1.5 text-sm border ${answers.nachhaltige.schwerpunkt === k ? "border-brand-primary bg-brand-primary text-white" : "border-neutral-300"}`}
                      data-testid={`nh-schwerpunkt-${k}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <ExplainerDetails title="Was heißt das?">
            <p>„Nachhaltige Investitionen" nach Art. 2 Nr. 17 der Offenlegungsverordnung (SFDR) sind Investitionen in Tätigkeiten, die zu einem Umwelt- oder Sozialziel beitragen, keinem Ziel wesentlich schaden und deren Zielunternehmen gute Governance einhalten. <strong>Beispiel:</strong> Ein Fonds kann angeben, dass 30 % seines Vermögens in nachhaltige Investitionen fließen.</p>
          </ExplainerDetails>
          <StepNav
            onBack={() => go(-1)}
            onNext={() => go(1)}
            onSkip={() => { setA({ nachhaltige: { active: false, min: 25, schwerpunkt: "egal" } }); go(1); }}
          />
        </section>
      )}

      {/* Schritt 5 – PAI */}
      {step.key === "pai" && (
        <section className="bg-white border border-neutral-200 p-8 md:p-10" data-testid="wizard-pai">
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">Sollen die wichtigsten nachteiligen Nachhaltigkeitsauswirkungen (PAI) berücksichtigt werden?</h2>
          <p className="text-sm text-neutral-600 mb-6">Weg 3 von 3.</p>
          <div className="space-y-3">
            <OptionButton selected={answers.pai.active === "ja"} onClick={() => setA({ pai: { ...answers.pai, active: "ja" } })} testId="pai-ja">
              Ja
            </OptionButton>
            <OptionButton selected={answers.pai.active === "nein"} onClick={() => setA({ pai: { active: "nein", themen: [] } })} testId="pai-nein">
              Nein
            </OptionButton>
            <OptionButton selected={answers.pai.active === "unsicher"} onClick={() => setA({ pai: { active: "unsicher", themen: [] } })} testId="pai-unsicher">
              Weiß ich nicht
            </OptionButton>
          </div>
          {answers.pai.active === "ja" && (
            <div className="mt-6">
              <div className="text-sm font-medium text-neutral-700 mb-3">Welche Themen sind Ihnen wichtig? (Mehrfachauswahl)</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {CONFIG.paiThemen.map((t) => {
                  const isAlle = t.key === "alle";
                  const checked = answers.pai.themen.includes(t.key);
                  return (
                    <CheckboxItem
                      key={t.key}
                      checked={checked}
                      testId={`pai-${t.key}`}
                      onChange={() => {
                        let next;
                        if (isAlle) {
                          next = checked ? [] : ["alle"];
                        } else {
                          next = checked
                            ? answers.pai.themen.filter((k) => k !== t.key)
                            : [...answers.pai.themen.filter((k) => k !== "alle"), t.key];
                        }
                        setA({ pai: { ...answers.pai, themen: next } });
                      }}
                    >
                      {t.label}
                    </CheckboxItem>
                  );
                })}
              </div>
            </div>
          )}
          <ExplainerDetails title="Was heißt das?">
            <p>PAI („Principal Adverse Impacts") beschreibt negative Folgen von Investitionen für Umwelt und Gesellschaft. Die Umsetzung kann je Anbieter sehr verschieden aussehen. <strong>Beispiel:</strong> Ein Fonds berücksichtigt CO₂-Emissionen und Wasserverbrauch aktiv, indem er Unternehmen mit besonders hohen Werten meidet.</p>
          </ExplainerDetails>
          <StepNav
            onBack={() => go(-1)}
            onNext={() => go(1)}
            onSkip={() => { setA({ pai: { active: "unsicher", themen: [] } }); go(1); }}
          />
        </section>
      )}

      {/* Schritt 6 – Persönliche Werte */}
      {step.key === "werte" && (
        <section className="bg-white border border-neutral-200 p-8 md:p-10" data-testid="wizard-werte">
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">Ergänzende persönliche Werte</h2>
          <p className="text-sm text-neutral-700 mb-2">
            Diese Wünsche gehören <strong>nicht</strong> zu den drei gesetzlichen Wegen, sind aber vielen Anlegern wichtig.
            Besprechen Sie sie ausdrücklich.
          </p>
          <p className="text-xs text-neutral-500 mb-6">
            Zur Einordnung: 62 % der erfassten nachhaltigen Anlagen in Deutschland schließen sämtliche Waffen aus. Quelle:{" "}
            <Link to="/nachhaltige-geldanlage-deutschland" className="text-brand-primary underline">FNG-Marktbericht 2026, Stichtag 31.12.2025</Link>.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {CONFIG.ergaenzendeWerte.map((v) => {
              const checked = answers.werte.auswahl.includes(v.key);
              return (
                <CheckboxItem
                  key={v.key}
                  checked={checked}
                  testId={`werte-${v.key}`}
                  onChange={() =>
                    setA({
                      werte: {
                        ...answers.werte,
                        auswahl: checked
                          ? answers.werte.auswahl.filter((k) => k !== v.key)
                          : [...answers.werte.auswahl, v.key],
                      },
                    })
                  }
                >
                  {v.label}
                </CheckboxItem>
              );
            })}
          </div>
          <div className="mt-5">
            <label className="text-sm font-medium text-neutral-700 mb-1 block" htmlFor="werte-sonstiges">
              Sonstiges (optional, max. 200 Zeichen)
            </label>
            <input
              id="werte-sonstiges"
              type="text"
              maxLength={200}
              value={answers.werte.sonstiges}
              onChange={(e) => setA({ werte: { ...answers.werte, sonstiges: e.target.value } })}
              className="w-full border border-neutral-300 px-3 py-2"
              data-testid="werte-sonstiges"
            />
          </div>
          <ExplainerDetails title="Was heißt das?">
            <p>Persönliche Ausschlüsse sind kein gesetzliches Kriterium der Nachhaltigkeitspräferenz, aber viele Berater dokumentieren sie zusätzlich. <strong>Beispiel:</strong> „Bitte investieren Sie mein Geld nicht in Unternehmen mit Umsätzen aus fossilen Brennstoffen über 5 %."</p>
          </ExplainerDetails>
          <StepNav
            onBack={() => go(-1)}
            onNext={() => go(1)}
            onSkip={() => { setA({ werte: { auswahl: [], sonstiges: "" } }); go(1); }}
          />
        </section>
      )}

      {/* Schritt 7 – Flexibilität */}
      {step.key === "flexibilitaet" && (
        <section className="bg-white border border-neutral-200 p-8 md:p-10" data-testid="wizard-flex">
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">Was soll passieren, wenn kein Produkt alle Ihre Vorgaben erfüllt?</h2>
          <div className="space-y-3">
            <OptionButton selected={answers.flexibilitaet === "anpassen"} onClick={() => setA({ flexibilitaet: "anpassen" })} testId="flex-anpassen">
              Ich möchte die Vorgaben gemeinsam mit meinem Berater anpassen
            </OptionButton>
            <OptionButton selected={answers.flexibilitaet === "verzichten"} onClick={() => setA({ flexibilitaet: "verzichten" })} testId="flex-verzichten">
              Ich möchte lieber auf ein Produkt verzichten als Vorgaben lockern
            </OptionButton>
            <OptionButton selected={answers.flexibilitaet === "unsicher"} onClick={() => setA({ flexibilitaet: "unsicher" })} testId="flex-unsicher">
              Ich bin unsicher
            </OptionButton>
          </div>
          <ExplainerDetails title="Was heißt das?">
            <p>Ihre Angaben werden üblicherweise im Beratungsprotokoll dokumentiert und können später geändert werden. <strong>Beispiel:</strong> Wenn Sie „lieber verzichten" wählen, wird Ihr Berater eher wenige, dafür konforme Produkte anbieten – oder gar keines.</p>
          </ExplainerDetails>
          <StepNav
            onBack={() => go(-1)}
            onNext={() => go(1)}
            nextDisabled={!answers.flexibilitaet}
            onSkip={() => { setA({ flexibilitaet: "unsicher" }); go(1); }}
          />
        </section>
      )}

      {/* Schritt 8 – Name & Datum */}
      {step.key === "name" && (
        <section className="bg-white border border-neutral-200 p-8 md:p-10" data-testid="wizard-name">
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">Name und Datum (optional, nur für das PDF)</h2>
          <p className="text-sm text-neutral-600 mb-6">
            Diese Angaben werden nicht gespeichert. Sie erscheinen nur in Ihrem PDF-Ausdruck.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block" htmlFor="name-input">Name</label>
              <input
                id="name-input"
                type="text"
                maxLength={80}
                value={answers.name}
                onChange={(e) => setA({ name: e.target.value })}
                className="w-full border border-neutral-300 px-3 py-2"
                data-testid="input-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block" htmlFor="date-input">Datum</label>
              <input
                id="date-input"
                type="date"
                value={answers.datum}
                onChange={(e) => setA({ datum: e.target.value })}
                className="w-full border border-neutral-300 px-3 py-2"
                data-testid="input-datum"
              />
            </div>
          </div>
          <StepNav onBack={() => go(-1)} onNext={() => go(1)} nextLabel="Ergebnis anzeigen" onSkip={() => go(1)} />
        </section>
      )}

      {/* Schritt 9 – Ergebnis */}
      {step.key === "ergebnis" && (
        <Result answers={answers} reset={reset} onBack={() => setStepIdx(STEPS.length - 2)} presetKey={presetKey} />
      )}
    </div>
  );
}

function Result({ answers, reset, onBack, presetKey }) {
  const profil = useMemo(() => buildProfil(answers), [answers]);
  const musterabsatz = useMemo(() => buildMusterabsatz(answers), [answers]);
  const fragen = useMemo(() => buildFragenAnBerater(answers), [answers]);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(musterabsatz);
      toast.success("Musterabsatz kopiert.");
    } catch {
      toast.error("Kopieren nicht möglich. Bitte markieren und Strg+C.");
    }
  };

  const handlePdf = () => {
    try {
      generatePdf(answers);
    } catch (e) {
      toast.error("PDF-Erzeugung fehlgeschlagen.");
    }
  };

  const kein = answers.grundfrage === "nein";
  const preset = presetKey ? PRESETS.find((p) => p.key === presetKey) : null;

  return (
    <section data-testid="wizard-ergebnis" aria-live="polite">
      <div className="bg-white border border-neutral-200 p-8 md:p-10 mb-6">
        <div className="overline mb-3">Ergebnis</div>
        <h2 className="font-serif text-3xl md:text-4xl font-medium mb-4">Ihr Nachhaltigkeitsprofil</h2>
        {preset && (
          <div className="inline-flex items-center gap-2 text-xs bg-brand-paper border border-brand-primary text-brand-dark px-3 py-1.5 mb-4" data-testid="preset-badge">
            Basiert auf Profil <strong className="text-brand-primary">{preset.title}</strong> ·{" "}
            <button type="button" onClick={reset} className="underline">Neu starten</button>
          </div>
        )}
        {kein && (
          <p className="text-neutral-700 leading-relaxed mb-6">
            Sie möchten <strong>keine Nachhaltigkeitspräferenz</strong> berücksichtigen.
            Bitte teilen Sie das im Beratungsgespräch ausdrücklich mit. Sie können diese Angabe jederzeit ändern.
          </p>
        )}
        <div className="border border-neutral-200 overflow-x-auto">
          <table className="w-full min-w-[520px]">
            <thead className="bg-brand-paper border-b-2 border-brand-primary">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-neutral-700 font-semibold">Kriterium</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-neutral-700 font-semibold">Ihre Vorgabe</th>
              </tr>
            </thead>
            <tbody>
              {profil.map((r) => (
                <tr key={r.k} className="border-b border-neutral-100">
                  <td className="px-4 py-3 font-medium text-neutral-800">{r.k}</td>
                  <td className="px-4 py-3 text-neutral-700">{r.v || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!kein && (
        <>
          <div className="bg-white border border-neutral-200 p-8 md:p-10 mb-6">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <h3 className="font-serif text-2xl md:text-3xl font-medium">So können Sie es im Gespräch sagen</h3>
              <button
                type="button"
                onClick={copyText}
                className="inline-flex items-center gap-2 text-sm text-brand-primary border border-brand-primary px-3 py-1.5 hover:bg-brand-primary hover:text-white transition-colors"
                data-testid="copy-musterabsatz"
              >
                <Copy size={14} strokeWidth={1.75} /> Text kopieren
              </button>
            </div>
            <p className="text-neutral-800 leading-relaxed bg-brand-paper p-5 border-l-4 border-brand-primary">
              {musterabsatz}
            </p>
          </div>

          <div className="bg-white border border-neutral-200 p-8 md:p-10 mb-6" data-testid="fragen-liste">
            <h3 className="font-serif text-2xl md:text-3xl font-medium mb-4">Fragen an Ihren Berater</h3>
            <ul className="space-y-2">
              {fragen.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 w-4 h-4 border border-brand-primary shrink-0" aria-hidden />
                  <span className="text-neutral-800 leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      <div className="bg-white border border-neutral-200 p-8 md:p-10 mb-6">
        <h3 className="font-serif text-2xl md:text-3xl font-medium mb-4">Gut zu wissen</h3>
        <ul className="space-y-2 text-neutral-800 leading-relaxed">
          {CONFIG.gutZuWissen.map((s, i) => (
            <li key={i} className="flex items-start gap-2"><Check size={16} strokeWidth={2} className="text-brand-primary mt-1 shrink-0" /> {s}</li>
          ))}
        </ul>
      </div>

      <div className="bg-white border border-neutral-200 p-8 md:p-10 mb-6">
        <h3 className="font-serif text-xl font-medium mb-4">Weiterlesen</h3>
        <ul className="space-y-2">
          {CONFIG.weiterLinks.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="text-brand-primary underline">{l.label}</Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 p-4 mb-6" data-testid="disclaimer">
        {CONFIG.disclaimer}
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-brand-primary"
          data-testid="ergebnis-back"
        >
          <ArrowLeft size={16} strokeWidth={1.75} /> Zurück
        </button>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handlePdf}
            className="inline-flex items-center gap-2 bg-brand-primary text-white px-5 py-2.5 text-sm font-medium hover:bg-brand-hover"
            data-testid="download-pdf"
          >
            <Download size={16} strokeWidth={1.75} /> PDF herunterladen
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 border border-brand-primary text-brand-primary px-5 py-2.5 text-sm font-medium hover:bg-brand-primary hover:text-white"
            data-testid="print"
          >
            <Printer size={16} strokeWidth={1.75} /> Drucken
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-700 px-5 py-2.5 text-sm font-medium hover:border-brand-primary hover:text-brand-primary"
            data-testid="restart"
          >
            <RotateCcw size={16} strokeWidth={1.75} /> Neu starten
          </button>
        </div>
      </div>
    </section>
  );
}
